import React, { useState } from 'react';
import { AlertTriangle, Check, LogOut, Shield, TrendingUp, Users, X } from 'lucide-react';
import { getAllUsers, getLimitRequests, resolveLimitRequest, updateUserLimits } from '../api';
import type { LimitIncreaseRequest, User } from '../types';

interface SuperAdminDashboardProps {
  onNavigateHome?: () => void;
}

export default function SuperAdminDashboard({ onNavigateHome }: SuperAdminDashboardProps) {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<Omit<User, 'passwordHash'>[]>([]);
  const [requests, setRequests] = useState<LimitIncreaseRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'requests'>('requests');

  const refreshData = async () => {
    const [usersResponse, requestsResponse] = await Promise.all([
      getAllUsers(password),
      getLimitRequests(),
    ]);

    if (usersResponse.success && usersResponse.users) {
      setUsers(usersResponse.users);
    }

    if (requestsResponse.success && requestsResponse.requests) {
      setRequests(requestsResponse.requests);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== 'vivalavida') {
      setError('Incorrect superadmin password');
      return;
    }

    setError('');
    setLoading(true);
    setIsAuthenticated(true);

    try {
      await refreshData();
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserLimits = async (userId: string, eventLimit: number, guestLimit: number) => {
    try {
      const response = await updateUserLimits(password, { userId, eventLimit, guestLimit });

      if (response.success && response.user) {
        setUsers(users.map((user) => (user.id === userId ? response.user! : user)));
        alert('User limits updated successfully.');
      } else {
        alert(response.error || 'Failed to update user limits');
      }
    } catch (err) {
      alert('An error occurred while updating user limits');
    }
  };

  const handleResolveRequest = async (
    requestId: string,
    status: 'approved' | 'rejected',
    eventLimit?: number,
    guestLimit?: number,
  ) => {
    try {
      const response = await resolveLimitRequest(password, {
        requestId,
        status,
        eventLimit,
        guestLimit,
      });

      if (response.success) {
        await refreshData();
        alert(`Request ${status} successfully.`);
      } else {
        alert(response.error || 'Failed to resolve request');
      }
    } catch (err) {
      alert('An error occurred while resolving the request');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setUsers([]);
    setRequests([]);
  };

  if (!isAuthenticated) {
    return (
      <div className="auth-layout auth-layout--compact">
        <section className="auth-showcase">
          <div className="brand-lockup">
            <div className="brand-mark">
              <Shield size={18} />
              superadmin
            </div>
            <span className="eyebrow">Restricted access</span>
          </div>

          <div className="auth-copy">
            <h1>Review account limit requests and adjust organizer quotas.</h1>
            <p>This panel is separate from the main organizer flow and should stay limited to trusted administrators.</p>
          </div>
        </section>

        <aside className="auth-sidebar">
          <div className="auth-panel card">
            <span className="eyebrow">SuperAdmin Login</span>
            <h2>Authenticate to open the control panel.</h2>
            <p className="auth-panel-copy">Use the superadmin password to review limit requests and update user caps.</p>

            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label htmlFor="superadmin-password">SuperAdmin Password</label>
                <input
                  id="superadmin-password"
                  type="password"
                  className="login-input"
                  placeholder="Enter superadmin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {error && <div className="error-box">{error}</div>}

              <button type="submit" className="btn" disabled={loading}>
                {loading ? 'Loading panel...' : 'Open SuperAdmin Panel'}
              </button>

              <button type="button" className="btn btn-tertiary" onClick={onNavigateHome}>
                Back to Home
              </button>
            </form>
          </div>
        </aside>
      </div>
    );
  }

  const pendingRequests = requests.filter((request) => request.status === 'pending');
  const resolvedRequests = requests.filter((request) => request.status !== 'pending');

  return (
    <div className="container dashboard-shell">
      <section className="page-hero card">
        <div className="page-hero__content">
          <span className="eyebrow">SuperAdmin</span>
          <h1 className="page-title">
            <Shield size={28} />
            Control Panel
          </h1>
          <p className="page-copy">Manage organizer account limits and review submitted increase requests.</p>
        </div>

        <div className="dashboard-hero-aside">
          <div className="stat-tile">
            <span>Total Users</span>
            <strong>{users.length}</strong>
          </div>
          <div className="stat-tile">
            <span>Pending Requests</span>
            <strong>{pendingRequests.length}</strong>
          </div>
          <button className="btn btn-secondary" onClick={handleLogout}>
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </section>

      <div className="stats-grid">
        <div className="card stat-card stat-card--blue">
          <Users size={22} />
          <strong>{users.length}</strong>
          <span>Total Users</span>
        </div>
        <div className="card stat-card stat-card--amber">
          <AlertTriangle size={22} />
          <strong>{pendingRequests.length}</strong>
          <span>Pending Requests</span>
        </div>
        <div className="card stat-card stat-card--green">
          <TrendingUp size={22} />
          <strong>{resolvedRequests.length}</strong>
          <span>Resolved Requests</span>
        </div>
      </div>

      <div className="card panel-card">
        <div className="segmented-control">
          <button
            className={`btn ${activeTab === 'requests' ? '' : 'btn-secondary'}`}
            onClick={() => setActiveTab('requests')}
          >
            <TrendingUp size={16} />
            Limit Requests
          </button>
          <button
            className={`btn ${activeTab === 'users' ? '' : 'btn-secondary'}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} />
            All Users
          </button>
        </div>
      </div>

      {activeTab === 'requests' && (
        <div className="content-stack">
          {pendingRequests.length === 0 && resolvedRequests.length === 0 ? (
            <div className="card empty-state-panel empty-state-panel--card">
              <TrendingUp size={22} />
              <span>No limit increase requests.</span>
            </div>
          ) : (
            <>
              {pendingRequests.length > 0 && (
                <section className="content-stack">
                  <div className="section-heading">
                    <h2>Pending Requests</h2>
                    <p>Review these before organizers hit their current limits.</p>
                  </div>

                  {pendingRequests.map((request) => (
                    <article key={request.id} className="card request-card">
                      <div className="request-card__header">
                        <div>
                          <h3>{request.userName}</h3>
                          <p>{request.userEmail}</p>
                        </div>
                        <span className="status-pill status-pill--pending">Pending</span>
                      </div>

                      <div className="request-grid">
                        <div className="request-metric">
                          <span>Event Limit</span>
                          <strong>{request.currentEventLimit} to {request.requestedEventLimit}</strong>
                        </div>
                        <div className="request-metric">
                          <span>Guest Limit</span>
                          <strong>{request.currentGuestLimit} to {request.requestedGuestLimit}</strong>
                        </div>
                      </div>

                      {request.message && (
                        <div className="note-panel">
                          <span>Message</span>
                          <p>{request.message}</p>
                        </div>
                      )}

                      <div className="request-meta">
                        Requested: {new Date(request.createdAt).toLocaleString()}
                      </div>

                      <div className="button-row">
                        <button
                          className="btn"
                          onClick={() => handleResolveRequest(
                            request.id,
                            'approved',
                            request.requestedEventLimit,
                            request.requestedGuestLimit,
                          )}
                        >
                          <Check size={16} />
                          Approve
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleResolveRequest(request.id, 'rejected')}
                        >
                          <X size={16} />
                          Reject
                        </button>
                      </div>
                    </article>
                  ))}
                </section>
              )}

              {resolvedRequests.length > 0 && (
                <section className="content-stack">
                  <div className="section-heading">
                    <h2>Resolved Requests</h2>
                    <p>Recent decisions across approved and rejected requests.</p>
                  </div>

                  {resolvedRequests.map((request) => (
                    <article key={request.id} className="card request-card request-card--muted">
                      <div className="request-card__header">
                        <div>
                          <h3>{request.userName}</h3>
                          <p>{request.userEmail}</p>
                        </div>
                        <span className={`status-pill ${request.status === 'approved' ? 'status-pill--approved' : 'status-pill--rejected'}`}>
                          {request.status}
                        </span>
                      </div>
                      <div className="request-meta">
                        Resolved: {request.resolvedAt ? new Date(request.resolvedAt).toLocaleString() : 'N/A'}
                      </div>
                    </article>
                  ))}
                </section>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="content-stack">
          {users.length === 0 ? (
            <div className="card empty-state-panel empty-state-panel--card">
              <Users size={22} />
              <span>No users found.</span>
            </div>
          ) : (
            users.map((user) => (
              <article key={user.id} className="card user-admin-card">
                <div className="user-admin-card__header">
                  <div>
                    <h3>{user.name}</h3>
                    <p>{user.email}</p>
                  </div>
                  <span className="mono-chip">{user.id}</span>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Event Limit</label>
                    <input
                      type="number"
                      className="input"
                      defaultValue={user.eventLimit}
                      min="1"
                      onBlur={(e) => {
                        const newLimit = parseInt(e.target.value, 10);
                        if (newLimit !== user.eventLimit && newLimit >= 1) {
                          handleUpdateUserLimits(user.id, newLimit, user.guestLimit);
                        }
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Guest Limit</label>
                    <input
                      type="number"
                      className="input"
                      defaultValue={user.guestLimit}
                      min="1"
                      onBlur={(e) => {
                        const newLimit = parseInt(e.target.value, 10);
                        if (newLimit !== user.guestLimit && newLimit >= 1) {
                          handleUpdateUserLimits(user.id, user.eventLimit, newLimit);
                        }
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Events Created</label>
                    <div className="user-count-tile">{user.eventCount}</div>
                  </div>
                </div>

                <div className="request-meta">
                  Joined: {new Date(user.createdAt).toLocaleDateString()}
                </div>
              </article>
            ))
          )}
        </div>
      )}
    </div>
  );
}
